import { ManagerSubscriptionAccount, UserSubscriptionPlan } from "../../constants/GlobalTypesAndEnumsAndConstants";

/**
 * Where a paywall purchase is paid. Decided here, once, for both the purchase itself
 * (`onClickSubscriptionUpgradeButton` in HelperMethodsHook.tsx) and what the paywall promises about it
 * (the yearly guarantees in TwoStepUpsellStep2PlanPicker.tsx, see `isPurchaseRouteCoveredByYearlyGuarantees`),
 * so the two can never disagree.
 */
export enum SubscriptionPurchaseRoute {
  /**
   * Jungle's own checkout (Stripe): Stripe Checkout, or Stripe's billing portal for an account that already
   * has a subscription. The web, and the iPhone app in the US.
   */
  JungleStripeCheckout = "jungle_stripe_checkout",
  /** A first purchase through Google Play, in the Android app. */
  GooglePlayInAppPurchase = "google_play_in_app_purchase",
  /** A first purchase through the App Store, in the iOS app outside the US (or before the country is known). */
  AppStoreInAppPurchase = "app_store_in_app_purchase",
  /** An Apple or Google subscriber changing plan in that store's app: an in-app upgrade, or the store's own settings. */
  NativeStorePlanChange = "native_store_plan_change",
  /**
   * Historical route retained for compatibility; the resolver now directs existing store subscribers to their
   * store rather than attempting a Stripe portal without a Stripe binding.
   */
  StoreSubscriberSentToStripeBillingPortal = "store_subscriber_sent_to_stripe_billing_portal",
  /** An Apple or Google subscriber whose current platform cannot change that store's subscription. */
  ManageWhereTheSubscriptionWasBought = "manage_where_the_subscription_was_bought",
  /** An existing subscription whose billing provider cannot be verified. */
  SubscriptionManagementUnavailable = "subscription_management_unavailable",
}

/**
 * Inside the native app, whether new purchases go through the store's in-app purchase: always in the Android app
 * (Google Play), and in the iOS app unless the viewer is in the US (the App Store). US iOS viewers pay through
 * Jungle's Stripe checkout instead. An unknown country (null, not detected yet) counts as outside the US.
 */
export function isNativeStoreInAppPurchasePlatform(
  isRunningAsMobileApp: boolean,
  isAndroid: boolean,
  isIOS: boolean,
  currentCountryUserIsIn: string | null
): boolean {
  if (!isRunningAsMobileApp) return false;
  const isUserInUSA = currentCountryUserIsIn === "US";
  return isAndroid || (isIOS && !isUserInUSA);
}

/** Whether the viewer's subscription is billed by the store of the app they are in (Apple in iOS, Google in Android). */
export function isSubscriptionManagedByThisAppsStore(
  managerSubscriptionAccount: string | null | undefined,
  isAndroid: boolean,
  isIOS: boolean
): boolean {
  const isManagedByAppleOnIos = managerSubscriptionAccount === ManagerSubscriptionAccount.Apple && isIOS;
  const isManagedByGoogleOnAndroid = managerSubscriptionAccount === ManagerSubscriptionAccount.Google && isAndroid;
  return isManagedByAppleOnIos || isManagedByGoogleOnAndroid;
}

export type SubscriptionPurchaseRouteInputs = {
  isRunningAsMobileApp: boolean;
  isAndroid: boolean;
  isIOS: boolean;
  /** `pageStore.currentCountryUserIsIn`: the viewer's country from their IP address; null until detected */
  currentCountryUserIsIn: string | null;
  /** The derived access plan; an existing billing binding takes precedence over paid access. */
  currentUserSubscriptionPlan: UserSubscriptionPlan;
  /** `managerSubscriptionAccount` from `getUserSubscriptionPlanAndLimits` */
  managerSubscriptionAccount: string | null | undefined;
  /** A nonempty subscription ID, independent of effective paid access. */
  hasSubscriptionBinding: boolean;
  /**
   * `hasUserSubscribedBefore(user)`: the Stripe code sends such an account to Stripe's billing portal instead of a
   * new checkout. False for a subscriber whose cancellation is pending, who therefore gets a new checkout.
   */
  hasUserSubscribedBefore: boolean;
};

export function getSubscriptionPurchaseRoute({
  isRunningAsMobileApp,
  isAndroid,
  isIOS,
  currentCountryUserIsIn,
  managerSubscriptionAccount,
  hasSubscriptionBinding,
  hasUserSubscribedBefore,
}: SubscriptionPurchaseRouteInputs): SubscriptionPurchaseRoute {
  const isBilledByAppleOrGoogle = hasSubscriptionBinding &&
    (managerSubscriptionAccount === ManagerSubscriptionAccount.Apple ||
      managerSubscriptionAccount === ManagerSubscriptionAccount.Google);
  // An exam pass is a one-time Stripe payment kept over an ended subscription's id; any plan replaces it through
  // Jungle's checkout, on the web and in the iOS app in the US alike
  if (managerSubscriptionAccount === ManagerSubscriptionAccount.StripeExamPass && !isRunningAsMobileApp) {
    return SubscriptionPurchaseRoute.JungleStripeCheckout;
  }
  if (
    hasSubscriptionBinding &&
    managerSubscriptionAccount !== ManagerSubscriptionAccount.Stripe &&
    managerSubscriptionAccount !== ManagerSubscriptionAccount.StripeExamPass &&
    !isBilledByAppleOrGoogle
  ) {
    return SubscriptionPurchaseRoute.SubscriptionManagementUnavailable;
  }
  if (hasSubscriptionBinding && managerSubscriptionAccount === ManagerSubscriptionAccount.Stripe) {
    return SubscriptionPurchaseRoute.JungleStripeCheckout;
  }

  if (isRunningAsMobileApp) {
    const isInAppPurchasePlatform = isNativeStoreInAppPurchasePlatform(
      isRunningAsMobileApp,
      isAndroid,
      isIOS,
      currentCountryUserIsIn
    );
    if (
      isInAppPurchasePlatform &&
      isBilledByAppleOrGoogle &&
      isSubscriptionManagedByThisAppsStore(managerSubscriptionAccount, isAndroid, isIOS)
    ) {
      return SubscriptionPurchaseRoute.NativeStorePlanChange;
    }
    if (isBilledByAppleOrGoogle) {
      return SubscriptionPurchaseRoute.ManageWhereTheSubscriptionWasBought;
    }
    if (isInAppPurchasePlatform && !hasUserSubscribedBefore) {
      return isAndroid
        ? SubscriptionPurchaseRoute.GooglePlayInAppPurchase
        : SubscriptionPurchaseRoute.AppStoreInAppPurchase;
    }
    // A Stripe binding uses its portal; an unbound grant can start a purchase.
    return SubscriptionPurchaseRoute.JungleStripeCheckout;
  }

  if (isBilledByAppleOrGoogle) {
    return SubscriptionPurchaseRoute.ManageWhereTheSubscriptionWasBought;
  }
  return SubscriptionPurchaseRoute.JungleStripeCheckout;
}

/**
 * Whether a yearly purchase on this route carries the beat your score guarantee (the only guarantee for new yearly
 * purchases since 2026-10-21; terms at jungleai.com/guarantee): Jungle's own Stripe checkout, and a first purchase
 * through Google Play, both of which Jungle can refund itself (Stripe, and the Play Developer API). Never the App
 * Store, whose refunds Apple handles. Store plan changes are left out too: an in-app upgrade from Pro to Elite is an
 * upgrade charge, which the guarantee does not cover, and a downgrade opens the store's settings rather than selling anything.
 */
export function isPurchaseRouteCoveredByYearlyGuarantees(
  subscriptionPurchaseRoute: SubscriptionPurchaseRoute
): boolean {
  return (
    subscriptionPurchaseRoute === SubscriptionPurchaseRoute.JungleStripeCheckout ||
    subscriptionPurchaseRoute === SubscriptionPurchaseRoute.GooglePlayInAppPurchase
  );
}
